"use client";

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { VIEW_AS_PLAN_STORAGE_KEY, readViewAsPlan } from '@adrodd/core';

/**
 * Admin "view as" plan preview.
 *
 * Lets an admin preview the app's premium/free UI gating from either side —
 * paywalls, "N free chapters remaining" counters, upgrade prompts — WITHOUT
 * changing their own account. The chosen perspective is stored per-browser
 * (localStorage) and only honoured for admins; a normal user is never affected.
 *
 * SCOPE: this is a DISPLAY/VISIBILITY preview only — it never changes the
 * admin's real access. The preview is made SERVER-AWARE: the axios interceptor
 * (see lib/apiClient.ts) forwards the chosen plan as the `X-View-As-Plan`
 * header, and the backend routes its user-facing gating FLAGS (chapter `locked`,
 * `is_paid`, daily counters) through that header for admins only. So:
 *   - Chapter `locked` flags and is_paid now flip with the preview because the
 *     API recomputes what it DISPLAYS from the previewed plan. Hard access
 *     control (ChapterPolicy/StoryPolicy) and the real daily-read limit are
 *     untouched — the admin's real permissions never change.
 *   - Changing the perspective invalidates the plan-affected React Query caches
 *     (see setViewAs) so the UI refetches with the new header and updates.
 */

export type PlanTier = 'free' | 'paid';

// The localStorage key + reader live in lib/viewAsPlanStorage so the axios
// interceptor (frontend/src/lib/apiClient.ts) reads the exact same key without
// importing this React module. Re-exported here for existing consumers/tests.
export { VIEW_AS_PLAN_STORAGE_KEY };
const STORAGE_KEY = VIEW_AS_PLAN_STORAGE_KEY;

// Fallback free-tier daily allotment, used ONLY when the server hasn't sent
// tier_info.daily_free_limit yet (older payloads). Used to render a plausible
// "free chapters remaining" counter when an admin whose real plan is paid
// previews the free experience (the server never sends a real free count for a
// paid user). Server-side enforcement always uses the backend constant.
const FREE_TIER_DAILY_CHAPTERS_FALLBACK = 3;

export interface EffectivePlanContextType {
  /** The real plan of the authenticated user (authoritative, from the API). */
  realPlan: PlanTier;
  /** Admin-only preview override; null when not previewing. */
  viewAs: PlanTier | null;
  /** The plan UI gating should actually use (override when previewing, else real). */
  effectivePlan: PlanTier;
  /** effectivePlan === 'paid'. */
  isPaid: boolean;
  /** True while an admin is previewing a plan other than a passthrough of their real one. */
  isPreviewing: boolean;
  /** True when the current user is allowed to set the override (admins only). */
  canPreview: boolean;
  /** Set (or clear, with null) the preview perspective. No-op for non-admins. */
  setViewAs: (plan: PlanTier | null) => void;
  /**
   * Daily free-chapter count to display, reflecting the effective plan.
   * null = unlimited (paid). A number = free-tier remaining count.
   */
  effectiveDailyChaptersRemaining: number | null;
}

const EffectivePlanContext = createContext<EffectivePlanContextType | undefined>(undefined);

// Shared reader (same key the interceptor uses) so context state and the
// outgoing X-View-As-Plan header can never disagree.
const readStoredViewAs = readViewAsPlan;

export const EffectivePlanProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAdmin, loading } = useAuth();
  const queryClient = useQueryClient();
  const [viewAsRaw, setViewAsRaw] = useState<PlanTier | null>(() => readStoredViewAs());

  // Authoritative "is this user premium" signal. Prefer tier_info.real_is_paid,
  // which is ALWAYS the true account plan — tier_info.is_paid now reflects the
  // DISPLAY plan (it flips when the admin preview header is sent, so it must NOT
  // drive realPlan). Fall back to is_paid / plan / paid for older payloads.
  let realIsPaid: boolean;
  if (user?.tier_info?.real_is_paid != null) {
    realIsPaid = user.tier_info.real_is_paid;
  } else if (user?.tier_info?.is_paid != null) {
    realIsPaid = user.tier_info.is_paid;
  } else if (user?.plan != null) {
    realIsPaid = user.plan === 'paid';
  } else {
    realIsPaid = user?.paid ?? false;
  }
  const realPlan: PlanTier = realIsPaid ? 'paid' : 'free';

  // Only admins may preview. If a stale override is present for a non-admin
  // (e.g. an admin logged out and a normal user logged in on the same browser),
  // ignore it entirely so gating reflects their real plan. Until auth resolves
  // `isAdmin` is false for everyone, so the stored override is kept (not
  // judged) while loading, else a hard reload would wipe an admin's choice.
  const canPreview = isAdmin;
  const viewAs = canPreview || loading ? viewAsRaw : null;

  const setViewAs = useCallback(
    (plan: PlanTier | null) => {
      if (!canPreview) return;
      setViewAsRaw(plan);
      try {
        if (plan === null) {
          window.localStorage.removeItem(STORAGE_KEY);
        } else {
          window.localStorage.setItem(STORAGE_KEY, plan);
        }
      } catch {
        // Ignore storage failures (private mode, quota) — in-memory state still applies.
      }
      // The preview is server-aware: the API now returns plan-gated flags
      // (chapter `locked`, is_paid, daily counters) computed from the header the
      // interceptor sends. Invalidate every plan-affected query so the UI
      // refetches with the new perspective and visibly updates. The localStorage
      // write above happens first so in-flight refetches pick up the new header.
      queryClient.invalidateQueries({ queryKey: ['stories'] });   // stories list + detail + story chapters
      queryClient.invalidateQueries({ queryKey: ['chapters'] });  // chapter reader payloads
      queryClient.invalidateQueries({ queryKey: ['dashboard'] }); // dashboard analytics (plan_limited)
      queryClient.invalidateQueries({ queryKey: ['auth'] });      // current user (tier_info)
    },
    [canPreview, queryClient],
  );

  // Once auth has resolved and the user is not an admin, drop any in-memory
  // override so the app immediately reflects their real plan.
  useEffect(() => {
    if (!loading && !canPreview && viewAsRaw !== null) {
      setViewAsRaw(null);
    }
  }, [loading, canPreview, viewAsRaw]);

  const effectivePlan: PlanTier = viewAs ?? realPlan;
  const isPreviewing = viewAs !== null && viewAs !== realPlan;

  const effectiveDailyChaptersRemaining = useMemo<number | null>(() => {
    if (effectivePlan === 'paid') return null; // unlimited
    // Free perspective: prefer the real server-sent free count; if the real
    // account is paid (no free count exists) fall back to a full free allotment
    // so the admin can see the free-tier counter.
    const realRemaining = user?.tier_info?.daily_chapters_remaining;
    if (typeof realRemaining === 'number') return realRemaining;
    // Full free allotment: the backend-sent limit, falling back for old payloads.
    return user?.tier_info?.daily_free_limit ?? FREE_TIER_DAILY_CHAPTERS_FALLBACK;
  }, [effectivePlan, user?.tier_info?.daily_chapters_remaining, user?.tier_info?.daily_free_limit]);

  const value = useMemo<EffectivePlanContextType>(
    () => ({
      realPlan,
      viewAs,
      effectivePlan,
      isPaid: effectivePlan === 'paid',
      isPreviewing,
      canPreview,
      setViewAs,
      effectiveDailyChaptersRemaining,
    }),
    [realPlan, viewAs, effectivePlan, isPreviewing, canPreview, setViewAs, effectiveDailyChaptersRemaining],
  );

  return <EffectivePlanContext.Provider value={value}>{children}</EffectivePlanContext.Provider>;
};

/**
 * Centralized effective-plan hook. All client-side premium/free UI gating
 * should read from this instead of the raw `user.plan` / `user.tier_info` so
 * that the admin "view as" preview flows through in one place.
 */
export const useEffectivePlan = (): EffectivePlanContextType => {
  const ctx = useContext(EffectivePlanContext);
  if (ctx === undefined) {
    throw new Error('useEffectivePlan must be used within an EffectivePlanProvider');
  }
  return ctx;
};
